import { Trans, useLingui } from "@lingui/react/macro";
import { visibleTextFromMarkdown } from "@rakazo/core/message-quote";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  toast,
} from "@rakazo/ui-web";
import { Copy, Download, FileText, Share2 } from "lucide-react";
import { downloadArtifactBytes } from "../../lib/artifact-open";
import { desktopBridge } from "../../lib/desktop";

export function MessageShareMenu({ text }: { text: string }) {
  const { t } = useLingui();
  const nativeSharing = desktopBridge()?.sharing;
  const canShare = nativeSharing?.supported || typeof navigator.share === "function";

  async function copy(markdown: boolean) {
    try {
      await navigator.clipboard.writeText(markdown ? text : visibleTextFromMarkdown(text));
      toast.success(t`Copied`);
    } catch {
      toast.error(t`Could not copy. Try again.`);
    }
  }

  function save(markdown: boolean) {
    const content = markdown ? text : visibleTextFromMarkdown(text);
    downloadArtifactBytes(
      markdown ? "message.md" : "message.txt",
      markdown ? "text/markdown;charset=utf-8" : "text/plain;charset=utf-8",
      new TextEncoder().encode(content),
    );
  }

  async function share() {
    try {
      const plain = visibleTextFromMarkdown(text);
      if (nativeSharing?.supported) {
        if (!(await nativeSharing.text(plain)))
          toast.error(t`Sharing is not available on this device.`);
      } else if (typeof navigator.share === "function") {
        await navigator.share({ text: plain });
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      toast.error(t`Could not share. Try again.`);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-full border border-border bg-background px-3.5 text-sm font-medium outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring">
        <Share2 size={15} strokeWidth={1.8} aria-hidden="true" />
        <Trans>Share</Trans>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuItem onClick={() => void copy(false)}>
          <Copy size={16} />
          <Trans>Copy as text</Trans>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void copy(true)}>
          <FileText size={16} />
          <Trans>Copy as Markdown</Trans>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => save(false)}>
          <Download size={16} />
          <Trans>Save as text file</Trans>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => save(true)}>
          <Download size={16} />
          <Trans>Save as Markdown file</Trans>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={!canShare} onClick={() => void share()}>
          <Share2 size={16} />
          <Trans>Share via…</Trans>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
